import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AppShell from "./AppShell";

const industries = ["E-commerce", "SaaS", "Local services", "Agency/Consulting", "Healthcare", "Education", "Real estate", "Hospitality", "Finance", "Other"];
const sizes = ["Solo founder", "2–10", "11–50", "51–200", "200+"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    industry: "",
    target_market: "",
    business_location: "",
    business_goals: "",
    company_size: "",
    competitors: "",
    social_links_str: "",
    consent_data_collection: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.onboarded) navigate("/dashboard");
  }, [user, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const competitors = form.competitors.split(",").map((s) => s.trim()).filter(Boolean);
      const social_links = {};
      form.social_links_str.split(",").map((s) => s.trim()).filter(Boolean).forEach((u) => {
        if (u.includes("linkedin")) social_links.linkedin = u;
        else if (u.includes("twitter") || u.includes("x.com")) social_links.twitter = u;
        else if (u.includes("instagram")) social_links.instagram = u;
        else if (u.includes("facebook")) social_links.facebook = u;
        else social_links[u] = u;
      });
      await api.put("/profile", {
        industry: form.industry || null,
        target_market: form.target_market || null,
        business_location: form.business_location || null,
        business_goals: form.business_goals || null,
        company_size: form.company_size || null,
        competitors,
        social_links,
        consent_data_collection: form.consent_data_collection,
      });
      await refresh();
      toast.success("Profile saved.");
      navigate("/audits/new");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save profile");
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    setBusy(true);
    try {
      await api.put("/profile", { consent_data_collection: false });
      await refresh();
      navigate("/audits/new");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title="Tell us about your business" subtitle="// onboarding · step 1 of 1">
      <div className="max-w-4xl" data-testid="onboarding-page">
        <p className="text-lg text-muted-foreground max-w-2xl">
          A few details now will make every recommendation sharper. You can skip and come back later.
        </p>

        <form onSubmit={onSubmit} className="mt-10 grid md:grid-cols-2 gap-6">
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Industry</label>
            <select
              value={form.industry}
              onChange={(e) => update("industry", e.target.value)}
              data-testid="onboarding-industry-select"
              className="mt-1 w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-intl"
            >
              <option value="">Select an industry</option>
              {industries.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Company size</label>
            <select
              value={form.company_size}
              onChange={(e) => update("company_size", e.target.value)}
              data-testid="onboarding-size-select"
              className="mt-1 w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-intl"
            >
              <option value="">Select a size</option>
              {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Target market</label>
            <input
              value={form.target_market}
              data-testid="onboarding-target-input"
              onChange={(e) => update("target_market", e.target.value)}
              placeholder="e.g. UK-based small businesses"
              className="mt-1 w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-intl"
            />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Business location</label>
            <input
              value={form.business_location}
              data-testid="onboarding-location-input"
              onChange={(e) => update("business_location", e.target.value)}
              placeholder="City, Country"
              className="mt-1 w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-intl"
            />
          </div>
          <div className="md:col-span-2">
            <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Competitors (comma-separated URLs)</label>
            <input
              value={form.competitors}
              data-testid="onboarding-competitors-input"
              onChange={(e) => update("competitors", e.target.value)}
              placeholder="competitor1.com, competitor2.com"
              className="mt-1 w-full bg-card border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-intl"
            />
          </div>
          <div className="md:col-span-2">
            <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Social links (comma-separated)</label>
            <input
              value={form.social_links_str}
              data-testid="onboarding-social-input"
              onChange={(e) => update("social_links_str", e.target.value)}
              placeholder="linkedin.com/company/..., twitter.com/..."
              className="mt-1 w-full bg-card border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-intl"
            />
          </div>
          <div className="md:col-span-2">
            <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Business goals</label>
            <textarea
              rows={3}
              value={form.business_goals}
              data-testid="onboarding-goals-input"
              onChange={(e) => update("business_goals", e.target.value)}
              placeholder="e.g. Be the AI-recommended choice for accountants in Manchester"
              className="mt-1 w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-intl"
            />
          </div>
          <label className="md:col-span-2 flex items-start gap-3 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={form.consent_data_collection}
              data-testid="onboarding-consent-checkbox"
              onChange={(e) => update("consent_data_collection", e.target.checked)}
              className="mt-1"
            />
            I consent to AI Visibility OS using my anonymised business data to improve audit quality and product features.
          </label>

          <div className="md:col-span-2 flex items-center gap-4 pt-4">
            <button
              type="submit"
              disabled={busy}
              data-testid="onboarding-submit-button"
              className="px-6 py-3 bg-obsidian text-bone font-app font-bold uppercase tracking-wider text-sm hover:bg-intl transition-colors disabled:opacity-60"
            >
              Save & run my first audit
            </button>
            <button
              type="button"
              onClick={skip}
              disabled={busy}
              data-testid="onboarding-skip-button"
              className="px-6 py-3 border border-border font-app font-bold uppercase tracking-wider text-sm hover:bg-card transition-colors"
            >
              Skip
            </button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
